var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useEffect, useRef, useState } from 'react';
import globalStore from '../../store';
import Card from './Card';
import { DataContext } from '../../player/DataContext';
import '../../styles/widgets/cards/cardManipulative.scss';
import customEventHandler from '../../helper/customEvents';
import { playAudio } from '../../helper/audioHelper';
import { saveState } from '../../helper/stateManagerHelper';
import { registerMultiSelectSaveEntries } from '../../helper/multiSelectSaveCoordinator';
import { queueCardShuffleSaveEntry } from '../../helper/cardShuffleSaveCoordinator';
import SelectFocusOverlay from '../../components/SelectFocusOverlay';
import Label from '../../components/Label';
import { t } from 'i18next';
import { announce } from '../../helper/announcer';
import { useDuplicateAnnounce, useCreationAnnounce } from '../../helper/hooks/useDuplicateAnnounce';
import { getImageSrc } from '../../helper/imageBlobCache';
import { shuffleArray } from '../../helper/shuffleArray';
import { v4 as uuidv4 } from 'uuid';
import { useMultiSelectDragSource } from '../../helper/hooks/useMultiSelectDrag';
var IS_TOUCH_DEVICE = typeof window !== 'undefined' && !!((_a = window.matchMedia) === null || _a === void 0 ? void 0 : _a.call(window, '(pointer: coarse)').matches);
// interface cardManipulativeInterface {
//   id: string;
//   center: cardsData['center'];
//   cards: cardsData['cards'];
//   positionLock: boolean;
//   rotate: boolean;
// }
var CardManipulative = function (props) {
    var _a, _b, _c, _d, _e;
    var id = props.id, center = props.center, cards = props.cards, positionLock = props.lock.positioning, rotate = props.rotate, dimensions = props.dimensions, scale = props.scale, backImage = props.backImage, label = props.label, hideEmptyDeck = props.hideEmptyDeck, infiniteCard = props.infiniteCard, deckType = props.deckType, deckSizeOnly = props.deckSizeOnly;
    var Z_BASE = 10;
    var zoomLevel = globalStore.useStoreState(function (store) { return store.playerStore; }).zoomLevel;
    var _f = useState(false), isFocused = _f[0], setIsFocused = _f[1];
    var duplicateLabel = useDuplicateAnnounce(id, t('text.manipulativeCardDeck'));
    var creationLabel = useCreationAnnounce(id, t('text.manipulativeCardDeck'));
    /** ---------------- DATA STORE FROM GAME CONTROLLER ---------------- */
    var _g = useContext(DataContext), setManipulative = _g.setManipulative, manipulative = _g.manipulative, selectedTool = _g.selectedTool, setSelectedTool = _g.setSelectedTool, setActiveManipulator = _g.setActiveManipulator, setManiplulatorComponentVisible = _g.setManiplulatorComponentVisible, setSelectedManipulativeId = _g.setSelectedManipulativeId, setContextMenu = _g.setContextMenu, panningEnable = _g.panningEnable;
    var _h = useMultiSelectDragSource(id, selectedTool), startMultiDrag = _h.startMultiDrag, moveMultiDrag = _h.moveMultiDrag, endMultiDrag = _h.endMultiDrag;
    var cardRef = useRef(null);
    var inputSourceRef = useRef(null);
    var keyMoveStartRef = useRef(null);
    var keyMoveCurPosRef = useRef(null);
    // Multi-select move refs
    var dragFinalPosition = useRef({ x: 0, y: 0 });
    var selectedToolRef = useRef(selectedTool);
    var manipulativeRef = useRef(manipulative);
    useEffect(function () {
        selectedToolRef.current = selectedTool;
    }, [selectedTool]);
    useEffect(function () {
        manipulativeRef.current = manipulative;
        // The deal/refill update has committed; allow the next deck click.
        isDealingRef.current = false;
    }, [manipulative]);
    var deckNumber = manipulative.filter(function (m) { return m.data.type === 'deck'; }).findIndex(function (m) { return m.data.id === id; }) + 1;
    var _j = useState(center), position = _j[0], setPosition = _j[1];
    var _k = useState(positionLock), lock = _k[0], setLock = _k[1];
    var _l = useState(null), dragStart = _l[0], setDragStart = _l[1];
    var _m = useState(null), dragStartItem = _m[0], setDragStartItem = _m[1];
    var dragStartRef = useRef(null);
    var hasMoved = useRef(false);
    var dragStated = useRef(false);
    // iPad VoiceOver double-tap fires touchstart → touchend → click(detail===0).
    // The touchend runs dealCard, then the synthetic click opens the context
    // menu — resulting in both a revealed card and an open menu. Defer the
    // deal so the click handler can cancel it before it runs. Same pattern as
    // DiceManipulative's deferredRollTimer.
    var deferredDealTimer = useRef(null);
    // Coalesces rapid deck clicks. A deal/refill stays "in flight" until React
    // commits the resulting state (released in the manipulative-sync effect
    // below). Without this, a second click runs its handler against a stale
    // `manipulative` snapshot and its setManipulative clobbers the first update —
    // which wedges the deck at the empty/refill boundary (shuffle/flip sound
    // plays but cards never move, and nothing but a reload recovers it).
    var isDealingRef = useRef(false);
    useEffect(function () { return function () {
        if (deferredDealTimer.current) {
            clearTimeout(deferredDealTimer.current);
            deferredDealTimer.current = null;
        }
    }; }, []);
    var getPoint = function (e) {
        if ('touches' in e && e.touches.length > 0) {
            return { x: e.touches[0].clientX, y: e.touches[0].clientY };
        }
        if ('changedTouches' in e && e.changedTouches.length > 0) {
            return { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
        }
        return {
            x: e.clientX,
            y: e.clientY,
        };
    };
    var startDrag = function (e) {
        var _a = getPoint(e), clientX = _a.x, clientY = _a.y;
        dragStartRef.current = {
            x: clientX,
            y: clientY,
        };
        hasMoved.current = false;
        setDragStart({
            x: clientX - position.x * zoomLevel,
            y: clientY - position.y * zoomLevel,
        });
        var foundItem = manipulative.filter(function (m) { return m.data.type === 'deck' && m.data.id === id; });
        setDragStartItem(foundItem.length > 0 ? JSON.parse(JSON.stringify(foundItem[0].data.center)) : null);
        dragStated.current = true;
        startMultiDrag();
    };
    var onTouchStart = function (e) {
        if (panningEnable)
            return;
        e.preventDefault();
        e.stopPropagation();
        if (inputSourceRef.current === 'touch')
            return;
        inputSourceRef.current = 'touch';
        startDrag(e);
    };
    var onMouseDown = function (e) {
        if (panningEnable)
            return;
        // Ignore right-click — it opens the context menu, not a drag/deal
        if (e.button === 2)
            return;
        e.preventDefault();
        e.stopPropagation();
        // Ignore synthetic mouse events after touch
        if (inputSourceRef.current === 'touch')
            return;
        inputSourceRef.current = 'mouse';
        startDrag(e);
    };
    useEffect(function () {
        var handleMove = function (e) {
            var _a, _b;
            if ('touches' in e) {
                e.preventDefault();
            }
            if (!dragStartRef.current)
                return;
            if (!lock) {
                var _c = getPoint(e), clientX = _c.x, clientY = _c.y;
                // movement threshold
                var dx = clientX - dragStartRef.current.x;
                var dy = clientY - dragStartRef.current.y;
                if (!hasMoved.current && Math.sqrt(dx * dx + dy * dy) > 5) {
                    hasMoved.current = true;
                }
                if (!hasMoved.current)
                    return;
                var newX_1 = (clientX - ((_a = dragStart === null || dragStart === void 0 ? void 0 : dragStart.x) !== null && _a !== void 0 ? _a : 0)) / zoomLevel;
                var newY_1 = (clientY - ((_b = dragStart === null || dragStart === void 0 ? void 0 : dragStart.y) !== null && _b !== void 0 ? _b : 0)) / zoomLevel;
                setManipulative(function (prev) {
                    return prev.map(function (item) {
                        return item.data.id === id
                            ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: newX_1, y: newY_1 } }) }) : item;
                    });
                });
                setPosition({ x: newX_1, y: newY_1 });
                dragFinalPosition.current = { x: newX_1, y: newY_1 };
                // Multi-select: broadcast delta so other selected items follow
                if (dragStartItem) {
                    moveMultiDrag(newX_1 - dragStartItem.x, newY_1 - dragStartItem.y);
                }
            }
        };
        var handleEnd = function () {
            if (!dragStart)
                return;
            // Multi-select: save this deck's own position change; followers' own
            // position changes are saved centrally by useMultiSelectFollowerEngine.
            var wasMoving = hasMoved.current;
            if (wasMoving && selectedToolRef.current.length > 1 && selectedToolRef.current.includes(id)) {
                if (dragStartItem) {
                    var entry = {
                        type: id,
                        id: id,
                        propertyName: 'center',
                        old: JSON.stringify(dragStartItem),
                        new: JSON.stringify(dragFinalPosition.current),
                    };
                    if (!registerMultiSelectSaveEntries(id, [entry])) {
                        saveState(entry);
                    }
                }
                endMultiDrag();
                dragStated.current = false;
                dragStartRef.current = null;
                setTimeout(function () {
                    inputSourceRef.current = null;
                }, 0);
                setDragStartItem(null);
                setDragStart(null);
                return;
            }
            if (!hasMoved.current) {
                if (IS_TOUCH_DEVICE) {
                    // Touch: defer so an iPad VoiceOver double-tap (which fires a
                    // synthetic click with detail===0 right after) can cancel it.
                    // Clear any still-pending deferred deal so two quick taps can't
                    // queue two deals.
                    if (deferredDealTimer.current) {
                        clearTimeout(deferredDealTimer.current);
                        deferredDealTimer.current = null;
                    }
                    deferredDealTimer.current = setTimeout(function () {
                        deferredDealTimer.current = null;
                        dealCard();
                    }, 120);
                }
                else {
                    dealCard();
                }
            }
            else {
                var _oldData = dragStartItem !== null ? dragStartItem : undefined;
                var _newData = dragFinalPosition.current;
                if (_oldData) {
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'center',
                        old: JSON.stringify(_oldData),
                        new: JSON.stringify(_newData),
                    });
                }
                // Move the deck to the end of the manipulative array so the dropped
                // deck renders above its previous neighbors (matches Spinner/Dice).
                setManipulative(function (prev) {
                    var index = prev.findIndex(function (item) { return item.data.id === id; });
                    if (index === -1)
                        return prev;
                    var updated = __spreadArray([], prev, true);
                    var movedItem = updated.splice(index, 1)[0];
                    updated.push(movedItem);
                    return updated;
                });
            }
            dragStated.current = false;
            dragStartRef.current = null;
            setTimeout(function () {
                inputSourceRef.current = null;
            }, 0);
            setDragStartItem(null);
            setDragStart(null);
        };
        if (dragStart !== null && !panningEnable) {
            window.addEventListener('mousemove', handleMove);
            window.addEventListener('mouseup', handleEnd);
            window.addEventListener('touchmove', handleMove, { passive: false });
            window.addEventListener('touchend', handleEnd);
            window.addEventListener('touchcancel', handleEnd);
        }
        return function () {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleEnd);
            window.removeEventListener('touchmove', handleMove);
            window.removeEventListener('touchend', handleEnd);
            window.removeEventListener('touchcancel', handleEnd);
        };
    }, [
        JSON.stringify(dragStartItem),
        dragStart,
        lock,
        panningEnable,
        zoomLevel,
        JSON.stringify(manipulative),
    ]);
    var cardW = (((_b = (_a = cards === null || cards === void 0 ? void 0 : cards[0]) === null || _a === void 0 ? void 0 : _a.dimensions) === null || _b === void 0 ? void 0 : _b.width) || dimensions.width) * scale;
    var cardH = (((_d = (_c = cards === null || cards === void 0 ? void 0 : cards[0]) === null || _c === void 0 ? void 0 : _c.dimensions) === null || _d === void 0 ? void 0 : _d.height) || dimensions.height) * scale;
    var cardWrapperDimension = {
        x: position.x + (dimensions.width * scale) / 2 + 20,
        y: position.y - (dimensions.height * scale) / 2,
        width: cardW + 40,
        height: Math.max(dimensions.height * scale, cardH + 40),
    };
    // const getLastPlacedCard = (cards: cardsData['cards']) => {
    //   for (let i = cards.length - 1; i >= 0; i--) {
    //     if (cards[i].placedOnDeck && cards[i].position) {
    //       return cards[i];
    //     }
    //   }
    //   return null;
    // };
    var getRandomCardPosition = function (existingPositions) {
        var _a, _b, _c, _d;
        if (existingPositions === void 0) { existingPositions = []; }
        var MIN_DISTANCE = 30;
        var MAX_ATTEMPTS = 50;
        var cardW = (((_b = (_a = cards === null || cards === void 0 ? void 0 : cards[0]) === null || _a === void 0 ? void 0 : _a.dimensions) === null || _b === void 0 ? void 0 : _b.width) || 0) * scale;
        var cardH = (((_d = (_c = cards === null || cards === void 0 ? void 0 : cards[0]) === null || _c === void 0 ? void 0 : _c.dimensions) === null || _d === void 0 ? void 0 : _d.height) || 0) * scale;
        var minX = cardWrapperDimension.x + cardW / 2;
        var minY = cardWrapperDimension.y + cardH / 2;
        var maxX = cardWrapperDimension.x + cardWrapperDimension.width - cardW / 2;
        var maxY = cardWrapperDimension.y + cardWrapperDimension.height - cardH / 2;
        var x = minX;
        var y = minY;
        var isTooClose = function (pos) {
            return existingPositions.some(function (p) {
                var dx = pos.x - p.x;
                var dy = pos.y - p.y;
                return Math.sqrt(dx * dx + dy * dy) < MIN_DISTANCE;
            });
        };
        for (var attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
            x = Math.random() * (maxX - minX) + minX;
            y = Math.random() * (maxY - minY) + minY;
            if (!isTooClose({ x: x, y: y })) {
                break;
            }
        }
        return {
            x: Math.round(x),
            y: Math.round(y),
        };
    };
    useEffect(function () {
        if (center)
            setPosition(center);
    }, [center]);
    useEffect(function () {
        setLock(positionLock);
    }, [positionLock]);
    var prevSelectedToolRef = useRef([]);
    useEffect(function () {
        var wasFocused = prevSelectedToolRef.current.includes(id);
        var nowFocused = selectedTool.includes(id);
        prevSelectedToolRef.current = selectedTool;
        setIsFocused(nowFocused);
        if (nowFocused && !wasFocused && cardRef.current) {
            setTimeout(function () {
                var _a, _b;
                // Don't steal focus from the context menu
                if ((_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.closest('.shape-context-menu'))
                    return;
                (_b = cardRef.current) === null || _b === void 0 ? void 0 : _b.focus();
            }, 100);
        }
    }, [selectedTool]);
    /** ---------------- BOTTOM PANEL EVENT ---------------- */
    useEffect(function () {
        var onToolClick = function (data) {
            switch (data.id) {
                case 'edit': {
                    var current_1 = selectedToolRef.current;
                    var isThisDeck = current_1.includes(id) || cards.some(function (card) { return current_1.includes(card.id); });
                    if (!isThisDeck)
                        break;
                    setActiveManipulator('cards');
                    setManiplulatorComponentVisible(true);
                    setSelectedManipulativeId(id);
                    break;
                }
                case 'suffle': {
                    // -----> Shuffle Deck
                    // Skip shuffle when only 0 or 1 card remains in the deck
                    var unplacedForShuffle = cards.filter(function (c) { return !c.placedOnDeck; });
                    if (unplacedForShuffle.length <= 1)
                        break;
                    var thisDeck = manipulative.find(function (_item) { return _item.data.type === 'deck' && _item.data.id === id; });
                    if (!thisDeck)
                        break;
                    var updatedCards = thisDeck.data.cards;
                    // separate cards
                    var placed = updatedCards.filter(function (c) { return c.placedOnDeck; });
                    var unplaced = updatedCards.filter(function (c) { return !c.placedOnDeck; });
                    if (unplaced.length !== 0)
                        playAudio('shuffleDeck');
                    // shuffle only unplaced cards
                    var shuffledUnplaced = shuffleArray(unplaced);
                    var _oldData = thisDeck;
                    var _newData_1 = __assign(__assign({}, thisDeck), { data: __assign(__assign({}, thisDeck.data), { cards: __spreadArray(__spreadArray([], shuffledUnplaced, true), placed, true) }) });
                    // Shuffle is a broadcast action — every selected deck's own listener fires in the
                    // same dispatch tick, so route through the shuffle-specific coordinator instead of
                    // calling saveState() directly here. That collects every selected deck's entry from
                    // this click into ONE undo/redo step, instead of one step per deck.
                    queueCardShuffleSaveEntry({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'completeData',
                        old: JSON.stringify(_oldData.data),
                        new: JSON.stringify(_newData_1.data),
                    });
                    // Every selected deck's CardManipulative instance has its own listener for
                    // this same broadcast event, and each fires synchronously in the same tick,
                    // before any of them re-render. Merge into `prev` (not the closed-over
                    // `manipulative`) so this deck's update lands on top of whatever another
                    // selected deck's listener already applied in this same tick, instead of
                    // overwriting the whole array from a stale pre-click snapshot.
                    setManipulative(function (prev) {
                        return prev.map(function (_item) {
                            return _item.data.type === 'deck' && _item.data.id === id ? _newData_1 : _item;
                        });
                    });
                    {
                        var selectedDecks = selectedToolRef.current.filter(function (tid) { return tid.startsWith('deck_'); });
                        if (selectedDecks[0] === id) {
                            var allDecks_1 = manipulativeRef.current.filter(function (m) { return m.data.type === 'deck'; });
                            var details = selectedDecks
                                .map(function (deckId) {
                                var _a, _b;
                                var deckIdx = allDecks_1.findIndex(function (m) { return m.data.id === deckId; }) + 1;
                                var deckData = (_a = allDecks_1.find(function (m) { return m.data.id === deckId; })) === null || _a === void 0 ? void 0 : _a.data;
                                var remaining = (_b = deckData === null || deckData === void 0 ? void 0 : deckData.cards.filter(function (c) { return !c.placedOnDeck; }).length) !== null && _b !== void 0 ? _b : 0;
                                return t('text.deckDetail', { number: deckIdx, count: remaining });
                            })
                                .join(', ');
                            announce(t('text.deckShuffledAnnounce', { details: details }));
                        }
                    }
                    break;
                }
                case 'dealCard': {
                    var deckItem = manipulative.find(function (m) { return m.data.type === 'deck' && m.data.id === id; });
                    var hasCardsLeft = deckItem
                        ? deckItem.data.cards.some(function (c) { return !c.placedOnDeck; })
                        : false;
                    if (hasCardsLeft)
                        dealCard();
                    break;
                }
                case 'lock': // -----> position Lock
                    // console.log('lock trigger &&&');
                    // let _oldDataLock: manipulativeInterface | undefined;
                    // let _newDataLock: manipulativeInterface | undefined;
                    // const _newStateLock = manipulative.map(item => {
                    //   if (item.data.type === 'deck' && item.data.id === id) {
                    //     _oldDataLock = item;
                    //     _newDataLock = {
                    //       ...item,
                    //       data: {
                    //         ...item.data,
                    //         lock: { ...item.data.lock, positioning: !item.data.lock.positioning },
                    //       },
                    //     };
                    //     return _newDataLock;
                    //   } else {
                    //     return item;
                    //   }
                    // });
                    // if (_oldDataLock && _newDataLock) {
                    //   saveState({
                    //     type: id,
                    //     id: id.split('_')[1],
                    //     propertyName: 'completeData',
                    //     old: JSON.stringify(_oldDataLock),
                    //     new: JSON.stringify(_newDataLock),
                    //   });
                    // }
                    // setManipulative(() => _newStateLock);
                    break;
            }
        };
        var isAnyCardSelected = selectedTool.some(function (tid) { return cards.some(function (card) { return card.id === tid; }); });
        if (isFocused || isAnyCardSelected) {
            customEventHandler.addEventListener('bottomPanel', onToolClick);
        }
        return function () {
            customEventHandler.removeEventListener('bottomPanel', onToolClick);
        };
    }, [isFocused, selectedTool, JSON.stringify(manipulative)]);
    /** ---------------- RESTACK ANNOUNCEMENT (always active, deck persists) ---------------- */
    useEffect(function () {
        var onStack = function (data) {
            if (data.id !== 'stack')
                return;
            var selectedCards = selectedToolRef.current.filter(function (tid) { return tid.startsWith('card_'); });
            if (selectedCards.length === 0)
                return;
            // Find cards belonging to this deck
            var myCards = cards.filter(function (c) { return selectedCards.includes(c.id); });
            if (myCards.length === 0)
                return;
            // Only the first affected deck announces for all decks
            var allDecks = manipulativeRef.current.filter(function (m) { return m.data.type === 'deck'; });
            var firstAffectedDeck = allDecks.find(function (d) {
                return d.data.cards.some(function (c) { return selectedCards.includes(c.id); });
            });
            if (!firstAffectedDeck || firstAffectedDeck.data.id !== id)
                return;
            var deckCounts = {};
            selectedCards.forEach(function (cardId) {
                for (var i = 0; i < allDecks.length; i++) {
                    var deckCards = allDecks[i].data.cards;
                    if (deckCards.some(function (c) { return c.id === cardId; })) {
                        deckCounts[i + 1] = (deckCounts[i + 1] || 0) + 1;
                        break;
                    }
                }
            });
            var details = Object.entries(deckCounts)
                .map(function (_a) {
                var deck = _a[0], count = _a[1];
                return t('text.restackDetail', { count: count, deck: deck });
            })
                .join('. ');
            announce(details);
            // Focus the next available card on screen, or the deck if none remain
            setTimeout(function () {
                var _a, _b;
                var remainingCards = document.querySelectorAll('[data-manipulative-id^="card_"]');
                if (remainingCards.length > 0) {
                    (_a = remainingCards[remainingCards.length - 1]) === null || _a === void 0 ? void 0 : _a.focus();
                }
                else {
                    (_b = cardRef.current) === null || _b === void 0 ? void 0 : _b.focus();
                }
            }, 150);
        };
        customEventHandler.addEventListener('bottomPanel', onStack);
        return function () {
            customEventHandler.removeEventListener('bottomPanel', onStack);
        };
    }, [id, cards]);
    var dealCard = function () {
        var _a, _b;
        // Drop the click if a deal/refill is still settling — see isDealingRef.
        if (isDealingRef.current)
            return;
        // Read the freshest committed state, not this render's closure snapshot,
        // so the data we write back can't be stale.
        var deckItem = manipulativeRef.current.find(function (m) { return m.data.type === 'deck' && m.data.id === id; });
        if (!deckItem)
            return;
        var cardsCopy = __spreadArray([], deckItem.data.cards, true);
        var hasUnplaced = cardsCopy.some(function (c) { return !c.placedOnDeck; });
        // infiniteCard decks never run out: an empty-deck click appends a fresh,
        // shuffled clone of every card (new ids, drawCount bumped) onto the end
        // of the existing array instead of resetting in place. Already-dealt
        // cards are left completely untouched — they never go back into the
        // deck. Checked ahead of the IDM/SWP in-place refill branch so an
        // infiniteCard deck that also happens to be IDM/SWP takes this path.
        if (!hasUnplaced && infiniteCard && cardsCopy.length > 0) {
            var nextDrawCount_1 = Math.max.apply(Math, __spreadArray([0], cardsCopy.map(function (c) { var _a; return (_a = c.drawCount) !== null && _a !== void 0 ? _a : 0; }), false)) + 1;
            // Always clone from the ORIGINAL authored batch (drawCount undefined),
            // never from cardsCopy as a whole — cloning the full (already-grown)
            // array would double the deck size on every click instead of adding a
            // fixed-size batch each cycle.
            var initialRotate_1 = deckItem.data.initialRotate;
            var templateCards = cardsCopy.filter(function (c) { return c.drawCount === undefined; });
            var newBatch = templateCards.map(function (c) { return (__assign(__assign({}, c), { id: "card_".concat(uuidv4().replace(/-/g, '')), placedOnDeck: false, position: { x: 0, y: 0 }, zIndex: 0, showImage: initialRotate_1 ? 'back' : 'front', drawCount: nextDrawCount_1 })); });
            var shuffledNewBatch = shuffleArray(newBatch);
            var refilledDeckData_1 = __assign(__assign({}, deckItem.data), { cards: __spreadArray(__spreadArray([], cardsCopy, true), shuffledNewBatch, true) });
            saveState({
                type: id,
                id: id.split('_')[1],
                propertyName: 'completeData',
                old: JSON.stringify(deckItem.data),
                new: JSON.stringify(refilledDeckData_1),
            });
            playAudio('shuffleDeck');
            isDealingRef.current = true;
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.type === 'deck' && item.data.id === id
                        ? __assign(__assign({}, item), { data: refilledDeckData_1 }) : item;
                });
            });
            announce(t('text.deckRefilledAnnounce', { defaultValue: 'Deck refilled and shuffled' }));
            return;
        }
        // Cyclic draw: when the deck is empty, clicking refills + shuffles the
        // stack and returns — no card is dealt on this click. A subsequent click
        // deals normally from the freshly refilled deck. Applies to every deck
        // that isn't an infiniteCard deck (handled above), regardless of
        // isIDM/isSwp — a plain authored deck restacks the same way.
        if (!hasUnplaced && cardsCopy.length > 0) {
            var initialRotate_2 = deckItem.data.initialRotate;
            var reset = cardsCopy.map(function (c) { return (__assign(__assign({}, c), { placedOnDeck: false, position: { x: 0, y: 0 }, zIndex: 0, 
                // Reset to the same face the cards were dealt with on the first cycle
                // ('front', the merge default, unless the deck opts into initialRotate).
                // Using 'back' unconditionally here flipped cards facedown on the
                // second (post-refill) use.
                showImage: initialRotate_2 ? 'back' : 'front' })); });
            var shuffled = shuffleArray(reset);
            var refilledDeckData_2 = __assign(__assign({}, deckItem.data), { cards: shuffled });
            saveState({
                type: id,
                id: id.split('_')[1],
                propertyName: 'completeData',
                old: JSON.stringify(deckItem.data),
                new: JSON.stringify(refilledDeckData_2),
            });
            playAudio('shuffleDeck');
            isDealingRef.current = true;
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.type === 'deck' && item.data.id === id
                        ? __assign(__assign({}, item), { data: refilledDeckData_2 }) : item;
                });
            });
            announce(t('text.deckRefilledAnnounce', { defaultValue: 'Deck refilled and shuffled' }));
            return;
        }
        var firstIndex = (_a = cardsCopy
            .map(function (c, i) { return ({ c: c, i: i }); })
            .filter(function (_a) {
            var c = _a.c;
            return !c.placedOnDeck;
        })
            .shift()) === null || _a === void 0 ? void 0 : _a.i;
        if (firstIndex === undefined)
            return;
        var nextZ = Math.max.apply(Math, __spreadArray([Z_BASE], cardsCopy.filter(function (c) { return c.placedOnDeck; }).map(function (c) { var _a; return (_a = c.zIndex) !== null && _a !== void 0 ? _a : Z_BASE; }), false)) + 1;
        var cardCenter = getRandomCardPosition(cardsCopy.map(function (c) { return c.position; }));
        var initialRotate = deckItem.data.initialRotate;
        cardsCopy[firstIndex] = __assign(__assign(__assign({}, cardsCopy[firstIndex]), { placedOnDeck: true, position: cardCenter, zIndex: nextZ, scale: (_b = deckItem.data.scale) !== null && _b !== void 0 ? _b : 1 }), (initialRotate ? { showImage: 'back' } : {}));
        var newDeckData = __assign(__assign({}, deckItem.data), { cards: cardsCopy });
        saveState({
            type: id,
            id: id.split('_')[1],
            propertyName: 'completeData',
            old: JSON.stringify(deckItem.data),
            new: JSON.stringify(newDeckData),
        });
        playAudio('flipCards');
        isDealingRef.current = true;
        setManipulative(function (prev) {
            return prev.map(function (item) {
                return item.data.type === 'deck' && item.data.id === id ? __assign(__assign({}, item), { data: newDeckData }) : item;
            });
        });
        var remaining = cards.filter(function (c) { return !c.placedOnDeck; }).length;
        announce(t('text.dealtCardAnnounce', {
            current: cards.length - remaining + 1,
            total: cards.length,
        }));
    };
    var deckDisableImage = rotate
        ? './assets/images/manipulative/cards/popup/deckLandscapeDisable.svg'
        : './assets/images/manipulative/cards/popup/deckPortraitDisable.svg';
    var cardImages = {
        portrait: {
            frontFace: './assets/images/manipulative/cards/popup/cardFrontPortrait.svg',
            backFace: './assets/images/manipulative/cards/popup/cardBackPortrait.svg',
        },
        landscape: {
            frontFace: './assets/images/manipulative/cards/popup/cardFrontLandscape.svg',
            backFace: './assets/images/manipulative/cards/popup/cardBackLandscape.svg',
        },
    };
    var remainingCards = cards.filter(function (c) { return !c.placedOnDeck; }).length;
    var allCardsPlaced = remainingCards === 0;
    var prevAllCardsPlacedRef = useRef(allCardsPlaced);
    useEffect(function () {
        if (allCardsPlaced && !prevAllCardsPlacedRef.current && cards.length > 0) {
            announce(t('text.allCardsDealtAnnouncement', { count: cards.length }));
        }
        prevAllCardsPlacedRef.current = allCardsPlaced;
    }, [allCardsPlaced, cards.length]);
    var STACK_OFFSET = 4;
    var MAX_STACK = 10;
    var stackCount = Math.min(remainingCards, MAX_STACK);
    var backFaceImage = rotate ? cardImages.landscape.backFace : cardImages.portrait.backFace;
    var stackExtraHeight = stackCount > 1 ? (stackCount - 1) * STACK_OFFSET : 0;
    return (_jsxs(_Fragment, { children: [!hideEmptyDeck && (_jsxs("div", { id: "".concat(id, "_deck"), style: { zIndex: dragStart !== null ? 999 : undefined, position: 'relative' }, children: [_jsx("div", { style: {
                            position: 'absolute',
                            top: position.y - (dimensions.height + stackExtraHeight) / 2,
                            left: position.x - dimensions.width / 2,
                            transform: "scale(".concat(scale, ")"),
                            transformOrigin: 'center center',
                            width: dimensions.width,
                            height: dimensions.height + stackExtraHeight,
                            pointerEvents: 'none',
                        }, children: _jsx(SelectFocusOverlay, { id: id, title: t('text.selectDeck'), className: "deckSelectBtn", onAfterClick: function (e) {
                                var _a;
                                // Open context menu for keyboard activation (Enter/Space) or touch devices only
                                var isKeyboardClick = e.detail === 0;
                                if (!isKeyboardClick && !IS_TOUCH_DEVICE)
                                    return;
                                // VO may activate the overlay instead of the deck button — still
                                // cancel any pending deferred deal so no card gets revealed.
                                if (deferredDealTimer.current) {
                                    clearTimeout(deferredDealTimer.current);
                                    deferredDealTimer.current = null;
                                }
                                var rect = (_a = cardRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                                if (rect) {
                                    setContextMenu({ x: rect.right, y: rect.top, manipulativeId: id });
                                }
                            } }) }), _jsxs("button", { ref: cardRef, className: "cardPoolWrapper", "data-manipulative-id": id, onMouseDown: onMouseDown, onTouchStart: onTouchStart, tabIndex: 0, "aria-label": (_e = duplicateLabel !== null && duplicateLabel !== void 0 ? duplicateLabel : creationLabel) !== null && _e !== void 0 ? _e : t('text.cardDeckLabel', {
                            number: deckNumber,
                            remaining: remainingCards,
                            total: cards.length,
                            action: !allCardsPlaced ? t('text.pressEnterToDeal') : t('text.allCardsDealt'),
                        }), "aria-roledescription": t('text.cardDeck'), onClick: function (e) {
                            var _a;
                            // detail === 0 = keyboard Enter/Space or VoiceOver activation
                            // Open context menu (same as onKeyDown), not deal card
                            if (e.detail === 0 && !panningEnable) {
                                // Cancel any pending deferred deal from the preceding synthetic
                                // touch (iPad VoiceOver path) so double-tap only opens the menu.
                                if (deferredDealTimer.current) {
                                    clearTimeout(deferredDealTimer.current);
                                    deferredDealTimer.current = null;
                                }
                                if (!selectedTool.includes(id))
                                    setSelectedTool([id]);
                                var rect = (_a = cardRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                                if (rect)
                                    setContextMenu({ x: rect.right, y: rect.top, manipulativeId: id });
                            }
                        }, onKeyDown: function (e) {
                            var _a, _b;
                            if (panningEnable)
                                return;
                            // Context menu: Enter, Space, Shift+F10, ContextMenu key
                            if (e.key === 'Enter' ||
                                e.key === ' ' ||
                                e.key === 'ContextMenu' ||
                                (e.shiftKey && e.key === 'F10')) {
                                e.preventDefault();
                                e.stopPropagation();
                                if (!selectedTool.includes(id)) {
                                    setSelectedTool([id]);
                                }
                                var rect = (_a = cardRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                                if (rect) {
                                    setContextMenu({ x: rect.right, y: rect.top, manipulativeId: id });
                                }
                                return;
                            }
                            if (!lock && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                e.preventDefault();
                                var curPos = (_b = keyMoveCurPosRef.current) !== null && _b !== void 0 ? _b : position;
                                if (!keyMoveStartRef.current) {
                                    keyMoveStartRef.current = __assign({}, curPos);
                                }
                                var step = e.shiftKey ? 50 : 10;
                                var delta = {
                                    ArrowUp: { x: 0, y: -step },
                                    ArrowDown: { x: 0, y: step },
                                    ArrowLeft: { x: -step, y: 0 },
                                    ArrowRight: { x: step, y: 0 },
                                }[e.key];
                                var newCenter_1 = { x: curPos.x + delta.x, y: curPos.y + delta.y };
                                keyMoveCurPosRef.current = newCenter_1;
                                setPosition(newCenter_1);
                                setManipulative(function (prev) {
                                    return prev.map(function (item) {
                                        return item.data.id === id
                                            ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: newCenter_1 }) }) : item;
                                    });
                                });
                            }
                        }, onKeyUp: function (e) {
                            if (keyMoveStartRef.current &&
                                keyMoveCurPosRef.current &&
                                ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                var newPos = keyMoveCurPosRef.current;
                                saveState({
                                    type: id,
                                    id: id.split('_')[1],
                                    propertyName: 'center',
                                    old: JSON.stringify(keyMoveStartRef.current),
                                    new: JSON.stringify(newPos),
                                });
                                var dirs = [];
                                if (newPos.y < keyMoveStartRef.current.y)
                                    dirs.push(t('text.up'));
                                if (newPos.y > keyMoveStartRef.current.y)
                                    dirs.push(t('text.down'));
                                if (newPos.x < keyMoveStartRef.current.x)
                                    dirs.push(t('text.left'));
                                if (newPos.x > keyMoveStartRef.current.x)
                                    dirs.push(t('text.right'));
                                announce(t('text.manipulativeMoved', {
                                    name: t('text.manipulativeCardDeck'),
                                    direction: dirs.join(', '),
                                    x: Math.round(newPos.x),
                                    y: Math.round(newPos.y),
                                }));
                                keyMoveStartRef.current = null;
                                keyMoveCurPosRef.current = null;
                            }
                        }, style: {
                            touchAction: 'none',
                            top: position.y - (dimensions.height + stackExtraHeight) / 2,
                            left: position.x - dimensions.width / 2,
                            transform: "scale(".concat(scale, ")"),
                            transformOrigin: 'center center',
                            cursor: panningEnable ? 'inherit' : dragStart !== null ? 'grabbing' : 'grab',
                            boxShadow: isFocused ? "0px 0px 20px #5063AE" : 'none',
                            borderRadius: "8px",
                            width: dimensions.width,
                        }, children: [allCardsPlaced ? (_jsx("img", { style: { width: dimensions.width, height: dimensions.height }, src: getImageSrc(deckDisableImage), draggable: false, alt: t('text.emptyDeck') })) : (_jsx("div", { style: {
                                    position: 'relative',
                                    width: dimensions.width + 0.5,
                                    height: dimensions.height + stackExtraHeight,
                                }, children: Array.from({ length: stackCount }).map(function (_, i) { return (_jsx("img", { style: {
                                        width: dimensions.width,
                                        height: dimensions.height,
                                        position: i === 0 ? 'relative' : 'absolute',
                                        top: i * STACK_OFFSET,
                                        left: 0,
                                        zIndex: stackCount - i,
                                    }, src: getImageSrc(backFaceImage), draggable: false, "aria-hidden": "true", alt: "" }, i)); }) })), _jsx(Label, { text: label })] })] })), _jsx("div", { id: "".concat(id, "_deckCards"), style: { zIndex: 2, position: 'relative' }, children: cards === null || cards === void 0 ? void 0 : cards.map(function (item) {
                    var _a, _b, _c, _d;
                    if (item.placedOnDeck) {
                        // User-created (custom) decks render every dealt card at the deck's
                        // own fixed dimensions/orientation, never per-card image-derived
                        // sizes, so all cards in a custom deck stay uniform. Pre-made / IDM
                        // decks keep their per-card dimensions (which may vary by image).
                        var isCustomDeck = deckType === 'customDeck';
                        var cardDimension = isCustomDeck ? dimensions : item.dimensions;
                        var cardRotate = isCustomDeck ? rotate : ((_a = item.rotate) !== null && _a !== void 0 ? _a : rotate);
                        return (_jsx(Card, { id: item.id, center: item.position, dimension: cardDimension, cardText: item.text, flip: item.showImage === 'back', rotate: cardRotate, positionLock: item.lock.positioning, scale: (_b = item.scale) !== null && _b !== void 0 ? _b : scale, zIndex: (_c = item.zIndex) !== null && _c !== void 0 ? _c : Z_BASE, type: item.type, backImage: (_d = item.backImage) !== null && _d !== void 0 ? _d : backImage, deckSizeOnly: deckSizeOnly }, item.id));
                    }
                    else
                        return null;
                }) })] }));
};
export default CardManipulative;
